import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AdminGuides = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [userGuide, setUserGuide] = useState('');
  const [businessGuide, setBusinessGuide] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [userMode, setUserMode] = useState('visual');
  const [businessMode, setBusinessMode] = useState('visual');
  const userQuillRef = useRef(null);
  const businessQuillRef = useRef(null);

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    setLoading(true);
    try {
      const [userRes, businessRes] = await Promise.all([
        axios.get('/api/guide/user'),
        axios.get('/api/guide/business')
      ]);
      setUserGuide(userRes.data.content || '');
      setBusinessGuide(businessRes.data.content || '');
    } catch (err) {
      console.error('Errore nel caricamento delle guide:', err);
      setError('Errore nel caricamento delle guide.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const type = activeTab === 0 ? 'user' : 'business';
      const content = activeTab === 0 ? userGuide : businessGuide;

      await axios.put(`/api/admin/guide/${type}`, { content });
      setSuccess('Guida salvata con successo!');
    } catch (err) {
      console.error('Errore nel salvataggio della guida:', err);
      setError('Errore nel salvataggio della guida.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('image', file);

        const res = await axios.post('/api/admin/guide/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const imageUrl = res.data?.url;
        const targetRef = activeTab === 0 ? userQuillRef : businessQuillRef;
        const editor = targetRef.current?.getEditor();

        if (editor && imageUrl) {
          const range = editor.getSelection(true);
          const index = range ? range.index : editor.getLength();
          editor.insertEmbed(index, 'image', imageUrl, 'user');
          editor.setSelection(index + 1);
        }
      } catch (uploadError) {
        console.error('Errore upload immagine guida:', uploadError);
        setError('Errore nel caricamento dell\'immagine.');
      }
    };
  }, [activeTab]);

  const handleVideoUpload = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/*');
    input.click();

    input.onchange = async () => {
      const file = input.files && input.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('video', file);

        const res = await axios.post('/api/admin/guide/upload-video', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const videoUrl = res.data?.url;
        const targetRef = activeTab === 0 ? userQuillRef : businessQuillRef;
        const editor = targetRef.current?.getEditor();

        if (editor && videoUrl) {
          const range = editor.getSelection(true);
          const index = range ? range.index : editor.getLength();
          editor.insertEmbed(index, 'video', videoUrl, 'user');
          editor.setSelection(index + 1);
        }
      } catch (uploadError) {
        console.error('Errore upload video guida:', uploadError);
        setError('Errore nel caricamento del video.');
      }
    };
  }, [activeTab]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        image: handleImageUpload,
        video: handleVideoUpload
      }
    }
  }), [handleImageUpload, handleVideoUpload]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'link', 'image', 'video'
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestione Guide</Typography>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Salvataggio...' : 'Salva Modifiche'}
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Guida Alumni" />
          <Tab label="Guida Aziende" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 ? (
            <Box key="user-section">
              <Typography variant="h6" gutterBottom>
                Guida per gli Alumni
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Questa guida verrà mostrata agli alunni registrati sulla piattaforma.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  size="small"
                  variant={userMode === 'visual' ? 'contained' : 'outlined'}
                  onClick={() => setUserMode('visual')}
                  sx={{ mr: 1 }}
                >
                  Visuale
                </Button>
                <Button
                  size="small"
                  variant={userMode === 'html' ? 'contained' : 'outlined'}
                  onClick={() => setUserMode('html')}
                >
                  HTML
                </Button>
              </Box>
              <Box sx={{ mt: 2, minHeight: 400 }}>
                {userMode === 'visual' ? (
                  <ReactQuill
                    theme="snow"
                    value={userGuide}
                    onChange={setUserGuide}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: '350px', marginBottom: '50px' }}
                    ref={userQuillRef}
                  />
                ) : (
                  <TextField
                    value={userGuide}
                    onChange={(e) => setUserGuide(e.target.value)}
                    multiline
                    minRows={14}
                    fullWidth
                    placeholder="Inserisci HTML..."
                  />
                )}\n              </Box>
            </Box>
          ) : (
            <Box key="business-section">
              <Typography variant="h6" gutterBottom>
                Guida per le Aziende
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Questa guida verrà mostrata alle aziende registrate sulla piattaforma.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  size="small"
                  variant={businessMode === 'visual' ? 'contained' : 'outlined'}
                  onClick={() => setBusinessMode('visual')}
                  sx={{ mr: 1 }}
                >
                  Visuale
                </Button>
                <Button
                  size="small"
                  variant={businessMode === 'html' ? 'contained' : 'outlined'}
                  onClick={() => setBusinessMode('html')}
                >
                  HTML
                </Button>
              </Box>
              <Box sx={{ mt: 2, minHeight: 400 }}>
                {businessMode === 'visual' ? (
                  <ReactQuill
                    theme="snow"
                    value={businessGuide}
                    onChange={setBusinessGuide}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ height: '350px', marginBottom: '50px' }}
                    ref={businessQuillRef}
                  />
                ) : (
                  <TextField
                    value={businessGuide}
                    onChange={(e) => setBusinessGuide(e.target.value)}
                    multiline
                    minRows={14}
                    fullWidth
                    placeholder="Inserisci HTML..."
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminGuides;
